import {jsx} from 'panel';

import {InputEl} from 'common/components/input';
import {parseNumber} from 'common/util';
import {NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {InputChangeEvent, InputSize} from 'common/components/input/types';

import {FilterValueProps} from '../types';

const MULTIPLE_VALUES_OPERATORS = new Set<NumberOperator>([NumberOperator.Between, NumberOperator.NotBetween]);

export function FilterNumericValue({filter, handleFilterValueChange, readOnlyMode}: FilterValueProps) {
  const {filterValue, filterOperator} = filter;

  const inputEl = (currentValue: number, index: number, handleInput: (number) => void) => {
    const isInvalid = parseNumber(currentValue) === null;
    const shouldAutoFocus =
      isInvalid &&
      // auto-focus on first invalid field
      (Array.isArray(filterValue) ? index === filterValue.findIndex((value) => value === null) ?? 0 : index === 0);
    const value = currentValue?.toString(10) ?? ``;

    return (
      <InputEl
        key={`input-${index}-${filterOperator}`}
        class={{'is-placeholder': isInvalid}}
        attrs={{
          elref: `filterInput`,
          size: InputSize.Medium,
          type: `number`,
          placeholder: `value`,
          'auto-focus': !!shouldAutoFocus,
          'select-on-focus': true,
          readonly: readOnlyMode,
        }}
        props={{value}}
        on={{
          change: (ev: InputChangeEvent) => {
            const input = ev.target as HTMLInputElement;

            // when the updated value is not a valid input, revert back to previously committed input
            const updatedValue = parseNumber(ev.detail.value);
            if (updatedValue !== null) {
              handleInput(updatedValue);
            } else {
              input.value = value;
            }

            input.blur(); // user has most likely pressed "Enter", in which case they are likely finished so we want to lose focus
          },
        }}
      />
    );
  };

  // handle cases where the numeric filter requires 2 values
  if (MULTIPLE_VALUES_OPERATORS.has(filter.filterOperator as NumberOperator)) {
    const [minValue, maxValue] = filter.filterValue as number[];
    return (
      <>
        {inputEl(minValue, 0, (value) => handleFilterValueChange([value, maxValue]))}
        <div sel='.entry-filter-conjunction'>and</div>
        {inputEl(maxValue, 1, (value) => handleFilterValueChange([minValue, value]))}
      </>
    );
  }

  return <>{inputEl(filter.filterValue as number, 0, (value) => handleFilterValueChange(value))}</>;
}
